'use client'

import CodiceFiscale from 'codice-fiscale-js'

import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { IoCheckmarkOutline } from 'react-icons/io5'
import { toast } from 'react-toastify'
import {
  FIELDNAMES,
  INPUT_TYPES,
  KNOWN_FORM_VALUES,
} from '../Form/utils/constants'
import Input from '../Input'
import Modal from '../Modal'
import Select from '../Select'

const {
  DONATOR_FIRST_NAME,
  DONATOR_LAST_NAME,
  DONATOR_GENDER,
  DONATOR_CITY,
  DONATOR_PROVINCE,
} = FIELDNAMES
const { DEFAULT, MALE, FEMALE } = KNOWN_FORM_VALUES

function TaxCodeCalculatorModal({
  isOpen,
  onClose,
  name,
  classNames = {
    modalOverlay: 'modal-overlay',
    modalContent: 'modal-content',
    closeButton: 'close-button',
  },
}) {
  const formContext = useFormContext()
  const currentFormValues = formContext.watch()

  const [taxCodeFormValues, setTaxCodeFormValues] = useState(() => ({
    firstName: currentFormValues[DONATOR_FIRST_NAME] ?? '',
    lastName: currentFormValues[DONATOR_LAST_NAME] ?? '',
    genderTaxCode: currentFormValues[DONATOR_GENDER] ?? DEFAULT,
    birthPlace: currentFormValues[DONATOR_CITY] || '',
    province: currentFormValues[DONATOR_PROVINCE] || '',
  }))
  const [taxCode, setTaxCode] = useState('')

  const handleTaxCodeChange = (e) => {
    setTaxCodeFormValues({
      ...taxCodeFormValues,
      [e.target.name]: e.target.value,
    })
  }

  const notifyOnFail = (error) => {
    const errorLabel = String(error).replace('Error: ', '')
    toast.error(
      `Errore!
      ${errorLabel}`,
      {
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      },
      { delay: 500 }
    )
  }

  const notifyOnSuccess = () => {
    toast.success(
      'Codice fiscale calcolato con successo',
      {
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        icon: <IoCheckmarkOutline />,
      },
      { delay: 500 }
    )
  }

  const handleCalculate = () => {
    const {
      firstName: _firstName,
      lastName: _lastName,
      genderTaxCode: _genderTaxCode,
      date,
      birthPlace,
      province,
    } = taxCodeFormValues

    const firstName = _firstName || currentFormValues[DONATOR_FIRST_NAME]
    const lastName = _lastName || currentFormValues[DONATOR_LAST_NAME]
    const genderTaxCode =
      _genderTaxCode !== DEFAULT
        ? _genderTaxCode
        : currentFormValues[DONATOR_GENDER]

    try {
      if (
        !firstName ||
        !lastName ||
        !genderTaxCode ||
        !date ||
        !birthPlace ||
        !province
      ) {
        throw new Error('Compila tutti i campi per calcolare il codice fiscale')
      }

      const [year, month, day] = date.split('-')
      const taxCode = new CodiceFiscale({
        name: firstName,
        surname: lastName,
        gender: genderTaxCode,
        day: Number(day),
        month: Number(month),
        year: Number(year),
        birthplace: birthPlace,
        birthplaceProvincia: province,
      })

      if (taxCode?.code) {
        setTaxCode(taxCode.code)
        if (formContext) {
          formContext.setValue(name, taxCode.code)
        }
        notifyOnSuccess()
        onClose()
      }
    } catch (error) {
      console.error('Errore: ', error)
      notifyOnFail(error)
    }
  }

  const { firstName, lastName, genderTaxCode, date, birthPlace, province } =
    taxCodeFormValues

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      classNames={{
        modalOverlay: 'modal-overlay',
        modalContent: 'modal-content',
        closeButton: 'close-button',
      }}
    >
      <div className={'tax-code-calculator'}>
        <h2
          className={'tax-code-calculator__title'}
          id='modal-title'
        >
          Calcola il codice fiscale
        </h2>

        <div className={'tax-code-calculator__form'}>
          <Input
            label={'Nome'}
            name={'firstName'}
            type={INPUT_TYPES.TEXT}
            defaultValue={currentFormValues[DONATOR_FIRST_NAME] || ''}
            value={firstName ?? currentFormValues[DONATOR_FIRST_NAME]}
            onChange={handleTaxCodeChange}
            useFormContextIfAvailable={false}
            disableQueryState={true}
            placeholder={'Nome'}
            config={{
              pattern: {
                value: 'only-letters',
                message: 'Inserisci solo lettere',
              },
            }}
          />
          <Input
            label={'Cognome'}
            name={'lastName'}
            type={INPUT_TYPES.TEXT}
            defaultValue={currentFormValues[DONATOR_LAST_NAME] || ''}
            value={lastName ?? currentFormValues[DONATOR_LAST_NAME]}
            onChange={handleTaxCodeChange}
            useFormContextIfAvailable={false}
            disableQueryState={true}
            placeholder={'Cognome'}
          />
          <Select
            label={'Genere'}
            name={'genderTaxCode'}
            selectOptions={[
              {
                value: DEFAULT,
                label: 'Genere',
                disabled: true,
              },
              {
                value: MALE,
                label: 'Maschio',
              },
              {
                value: FEMALE,
                label: 'Femmina',
              },
            ]}
            defaultValue={currentFormValues[DONATOR_GENDER] || MALE}
            value={genderTaxCode ?? currentFormValues[DONATOR_GENDER]}
            useFormContextIfAvailable={false}
            disableQueryState={true}
            placeholder={'Genere'}
            onChange={handleTaxCodeChange}
          />

          <Input
            label={'Data di nascita'}
            name={'date'}
            type={INPUT_TYPES.DATE}
            value={date}
            onChange={handleTaxCodeChange}
            useFormContextIfAvailable={false}
            disableQueryState={true}
            placeholder={'GG/MM/AAAA'}
            config={{
              min: '1900-01-01',
              max: new Date().toISOString().split('T')[0],
              required: {
                value: true,
                message: 'Il nome è obbligatorio',
              },
            }}
          />
          <Input
            label={'Città'}
            name={'birthPlace'}
            type={INPUT_TYPES.TEXT}
            value={birthPlace}
            useFormContextIfAvailable={false}
            onChange={handleTaxCodeChange}
            disableQueryState={true}
            placeholder={'Luogo di nascita'}
          />
          <Input
            label={'Provincia'}
            name={'province'}
            type={INPUT_TYPES.TEXT}
            value={province}
            useFormContextIfAvailable={false}
            disableQueryState={true}
            onChange={handleTaxCodeChange}
            placeholder={'Provincia es. MI'}
          />
        </div>
        <button
          className={'tax-code-calculator__calculate-button'}
          onClick={handleCalculate}
        >
          Calcola Codice Fiscale
        </button>
      </div>
    </Modal>
  )
}

export default TaxCodeCalculatorModal
